import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IcChevronLeft,
  IcChevronRight,
  IcBanknote,
  IcWallet,
  IcAlertCircle,
  IcReceipt,
  IcPackage,
  IcUser,
  IcSparkles,
  IcHandCoins,
} from '../../theme/icons';
import { round3 } from '../../utils/money';
import VenteTabs from '../../components/VenteTabs';
import salesService from '../../services/salesService';

// The history paginates BY DAY: each "page" is one calendar day. A large page size
// makes sure every sale of the day comes back in a single fetch.
const DAY_PAGE_SIZE = 1000;

const fmtDateInput = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function MobileSellerHistory() {
  const navigate = useNavigate();

  // Pagination is by DAY — this is the day currently shown (defaults to today).
  const [currentDay, setCurrentDay] = useState(fmtDateInput(new Date()));

  // Data
  const [sales, setSales] = useState([]);
  const [creditPayments, setCreditPayments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ ca: 0, encaisse: 0, credit_paye: 0, credit: 0, count: 0, services_count: 0, services_total: 0 });

  // The date range is the single selected day (start = end = currentDay).
  const filterArgs = useMemo(() => ({
    startDate: currentDay,
    endDate: currentDay,
  }), [currentDay]);

  // Single fetch whenever the day changes.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const pageRes = await salesService.getSalesPage({ ...filterArgs, page: 1, pageSize: DAY_PAGE_SIZE });
        if (!active) return;
        setSales(pageRes.results);
        setTotalCount(pageRes.count);
      } catch (err) {
        if (!active) return;
        console.error('Error fetching sales:', err);
        setError('Erreur lors du chargement des ventes. Veuillez réessayer.');
        setSales([]);
        setTotalCount(0);
      } finally {
        if (active) setLoading(false);
      }
      // KPI aggregates for the selected day.
      salesService.getSalesSummary(filterArgs).then((s) => { if (active) setSummary(s); });
      // Encaissements crédit reçus ce jour-là (lignes dédiées, comme sur PC).
      salesService.getCreditPayments(filterArgs).then((rows) => { if (active) setCreditPayments(rows); });
    })();
    return () => { active = false; };
  }, [filterArgs]);

  // ---- Day navigation ----
  const isToday = currentDay === fmtDateInput(new Date());
  const shiftDay = (delta) => {
    const d = new Date(currentDay + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setCurrentDay(fmtDateInput(d));
  };
  const dayLabel = (() => {
    try {
      return new Date(currentDay + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    } catch (e) { return currentDay; }
  })();

  // ---- Helpers (copied from SellerHistorySales) ----
  const formatTime = (dateString) => {
    if (!dateString) return '';
    try { return new Date(dateString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch (err) { return ''; }
  };
  const getTotalPrice = (sale) => sale.total_price || sale.grand_total || 0;
  const getAmountPaid = (sale) => sale.amount_paid || sale.amount_payed || 0;
  const getAmountDue = (sale) => {
    if (sale.amount_due !== undefined) return sale.amount_due;
    if (sale.credit !== undefined) return sale.credit;
    return Math.max(0, getTotalPrice(sale) - getAmountPaid(sale));
  };
  const getCreditPaye = (sale) => sale.credit_paye || 0;
  const getPointsUsed = (sale) => parseFloat(sale.points_used) || 0;
  const getPaymentStatus = (sale) => {
    // Retour client (vente négative) : jamais « Payé ».
    if (sale.is_return) return { status: 'Retour', color: 'bdg-warn' };
    const amountPaid = getAmountPaid(sale);
    const amountDue = getAmountDue(sale);
    if (amountDue <= 0) {
      // Variants = grammaire Étiquettes Ki Para (index.css) — famille paiement.
      if (getCreditPaye(sale) > 0) return { status: 'Crédit payé', color: 'bdg-pos3' };
      if (getPointsUsed(sale) > 0 && amountPaid <= 0.0005) return { status: 'Payé avec points', color: 'bdg-pos' };
      return { status: 'Payé', color: 'bdg-pos' };
    }
    if (amountPaid > 0) return { status: 'Partiel', color: 'bdg-warn' };
    return { status: 'Non payé', color: 'bdg-critical' };
  };
  const clientLabel = (c) =>
    c?.name || `${c?.first_name || ''} ${c?.last_name || ''}`.trim() || 'Client de passage';

  // Comme la version PC : l'historique est affiché PAR PRODUIT (pas par client).
  // Chaque vente est aplatie en une carte par ligne produit/service ; les
  // encaissements crédit du jour ont leurs propres cartes teal, fusionnées
  // chronologiquement.
  const itemRows = useMemo(() => {
    const rows = [];
    for (const sale of sales) {
      const dt = sale.date_created || sale.date_added;
      (sale.details || []).forEach((d, i) => {
        rows.push({
          key: `${sale.id}-p${i}`, sale, type: 'product', dt,
          name: d.product_name || 'Produit',
          qty: d.quantity || 1,
          price: d.price || 0,
          total: d.total_detail != null ? d.total_detail : ((d.price || 0) * (d.quantity || 1)),
        });
      });
      (sale.service_details || []).forEach((s, i) => {
        const price = parseFloat(s.price) || 0;
        const remise = parseFloat(s.remise) || 0;
        const fp = remise > 0 ? price * (1 - remise / 100) : price;
        rows.push({
          key: `${sale.id}-s${i}`, sale, type: 'service', dt,
          name: s.service_name || s.service?.name || 'Service',
          qty: s.quantity || 1,
          price: fp,
          total: s.total_detail != null ? s.total_detail : (fp * (s.quantity || 1)),
        });
      });
    }
    for (const p of creditPayments) {
      rows.push({ key: `cp-${p.id}`, type: 'payment', dt: p.date, payment: p });
    }
    // Plus récent d'abord ; le tri est stable donc les lignes d'une même vente restent groupées.
    return rows.sort((a, b) => new Date(b.dt) - new Date(a.dt));
  }, [sales, creditPayments]);
  const productRowCount = itemRows.length - creditPayments.length;

  // CA et Encaissé incluent le crédit payé reçu ce jour (calcul côté backend).
  const KPIS = [
    { key: 'ca', label: "Chiffre d'affaires", value: `${summary.ca.toFixed(3)} DT`, icon: IcBanknote, tint: 'bg-blue-50 text-blue-600' },
    {
      key: 'services', label: `Services (${summary.services_count || 0})`,
      value: `${(summary.services_total || 0).toFixed(3)} DT`,
      icon: IcSparkles, tint: 'bg-green-50 text-green-700',
    },
    { key: 'encaisse', label: 'Encaissé', value: `${summary.encaisse.toFixed(3)} DT`, icon: IcWallet, tint: 'bg-green-50 text-green-600' },
    { key: 'credit_paye', label: 'Crédit payé', value: `${(summary.credit_paye || 0).toFixed(3)} DT`, icon: IcHandCoins, tint: 'bg-green-50 text-green-700' },
    { key: 'credit', label: 'Crédit', value: `${summary.credit.toFixed(3)} DT`, icon: IcAlertCircle, tint: 'bg-red-50 text-red-600' },
  ];

  return (
    <div className="w-full px-4 py-6">
      {/* Titre retiré (owner Studio 2026-08-14) — remplacé par le bouton
          déroulant VenteTabs + bascule Ventes/Services, comme /reservations. */}
      <VenteTabs />
      {/* Day navigation — one calendar day per page */}
      <div className="flex items-center justify-between gap-2 bg-white rounded-xl border border-gray-200 p-2 mb-4">
        {/* Nav par jour = famille btn-day (Règle Jour). */}
        <button
          onClick={() => shiftDay(-1)}
          aria-label="Jour précédent"
          className="btn-day h-9 w-9 rounded-xl transition-colors"
        >
          <IcChevronLeft />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          {/* icône calendrier retirée (owner Studio 2026-08-14) */}
          <span className="text-sm font-medium text-gray-800 capitalize truncate">{dayLabel}</span>
        </div>
        <button
          onClick={() => shiftDay(1)}
          disabled={isToday}
          aria-label="Jour suivant"
          className="btn-day h-9 w-9 rounded-xl transition-colors disabled:cursor-not-allowed"
        >
          <IcChevronRight />
        </button>
      </div>

      {!isToday && (
        /* « Aujourd'hui » = secondaire (Boutons §2). */
        <button
          onClick={() => setCurrentDay(fmtDateInput(new Date()))}
          className="btn-s mb-4 w-full py-2 rounded-xl text-sm transition-colors"
        >
          Revenir à aujourd'hui
        </button>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm">{error}</div>
      )}

      {/* Total du jour — carte KPI blanche (Composants §5), valeur CA en vert. */}
      <div className="kpi rounded-2xl p-4 mb-4">
        <div className="kpi-label uppercase text-xs tracking-normal">Chiffre d'affaires du jour</div>
        <div className="kpi-value text-2xl text-green-700 mt-0.5">{summary.ca.toFixed(3)} DT</div>
        <div className="kpi-sub text-xs mt-1">
          {totalCount} vente{totalCount !== 1 ? 's' : ''} · {productRowCount} produit{productRowCount !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 gap-2.5 mb-4">
        {KPIS.slice(1).map((k) => (
          <div key={k.key} className="bg-white rounded-2xl border border-gray-200 p-3 flex items-center gap-2.5">
            <span className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${k.tint}`}>
              <k.icon />
            </span>
            <div className="min-w-0">
              <div className="text-[11px] text-gray-400 truncate">{k.label}</div>
              <div className="text-sm font-bold text-gray-800 truncate tabular-nums">{k.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Liste par produit — une carte par ligne produit/service (comme la
          table PC), plus les encaissements crédit du jour en cartes teal. */}
      {loading && itemRows.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      ) : itemRows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <IcReceipt className="mx-auto text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Aucune vente ce jour</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {itemRows.map((row) => {
            if (row.type === 'payment') {
              const p = row.payment;
              return (
                <div
                  key={row.key}
                  onClick={() => { if (p.sale_id) navigate(`/vente/${p.sale_id}/receipt`); }}
                  className={`flex items-center gap-3 p-3 bg-green-50 rounded-2xl border border-green-100 transition-colors ${p.sale_id ? 'cursor-pointer hover:bg-green-100' : ''}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-green-100 text-green-700 flex-shrink-0 flex items-center justify-center">
                    <IcHandCoins />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-green-700 truncate">Encaissement crédit</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 min-w-0">
                      <IcUser className="text-gray-300 shrink-0" size={10} />
                      <span className="truncate">{p.client?.name || 'Client'}</span>
                      {formatTime(p.date) && <span className="text-gray-400 shrink-0">· {formatTime(p.date)}</span>}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="bdg bdg-pos3">
                        Crédit payé
                      </span>
                      <span className="text-sm font-semibold text-green-700 tabular-nums">
                        +{(p.amount || 0).toFixed(3)}
                        <span className="text-[11px] text-green-600 ml-0.5">DT</span>
                      </span>
                    </div>
                  </div>
                  {p.sale_id && <IcChevronRight className="text-gray-300 shrink-0" />}
                </div>
              );
            }
            const sale = row.sale;
            const ps = getPaymentStatus(sale);
            return (
              <div
                key={row.key}
                onClick={() => navigate(`/vente/${sale.id}/receipt`)}
                className="flex items-center gap-3 p-3 bg-white rounded-2xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <div className={`h-11 w-11 rounded-xl flex-shrink-0 flex items-center justify-center ${
                  row.type === 'product' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-700'
                }`}>
                  {row.type === 'product' ? <IcPackage /> : <IcSparkles />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate">{row.name}</span>
                    <span className="text-sm font-semibold text-gray-800 shrink-0 tabular-nums">
                      {row.total.toFixed(3)}
                      <span className="text-[11px] text-gray-500 ml-0.5">DT</span>
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 tabular-nums">
                    ×{Math.abs(row.qty)} · {row.price.toFixed(3)} DT / unité
                  </div>
                  <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 min-w-0">
                    <IcUser className="text-gray-300 shrink-0" size={10} />
                    <span className="truncate">{clientLabel(sale.client)}</span>
                    {formatTime(sale.date_created) && <span className="text-gray-400 shrink-0">· {formatTime(sale.date_created)}</span>}
                  </div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <span className={`bdg ${ps.color}`}>
                      {ps.status}
                    </span>
                    {getAmountDue(sale) > 0 && (
                      <span className="text-[11px] text-gray-400 tabular-nums">
                        Reste <span className="text-red-700 font-medium">{getAmountDue(sale).toFixed(3)}</span>
                      </span>
                    )}
                    {getPointsUsed(sale) > 0 && (
                      <span className="text-[11px] text-green-700 font-medium tabular-nums">
                        {round3(getPointsUsed(sale))} pts
                      </span>
                    )}
                  </div>
                </div>

                <IcChevronRight className="text-gray-300 shrink-0" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
